import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { usePageItems } from 'global/cnr/contexts/useHelpers/usePageItems';
import { useProfileItems } from 'global/cnr/contexts/useHelpers/useProfileItems';
import { SwiperWithTabsWithProvider } from 'global/components/swipers/SwiperWithTabz';
import _ from 'lodash';
import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { _checkComponentTimes } from '../../App';
import { subAppTypes } from '../../AppSub';
import { _sportsTournamentViewModes } from '../../projectSpecific/sports/cnr/reducers/SportsTournamentReducer';
import { AppSettingsContext } from '../cnr/contexts/AppSettingsContext';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import PageAreaProvider from '../cnr/contexts/PageAreaContext';
import PageAreaParentProvider from '../cnr/contexts/PageAreaParentContext';
import PageDataProvider from '../cnr/contexts/PageDataContext';
import { ParentContext } from '../cnr/contexts/ParentContext';
import RssProvider from '../cnr/contexts/RssContext';
import { startTypes } from '../cnr/reducers/StartReducer';
import { uniqueKey } from '../common/keys';
import PageError, { pageErrorReasonTypes } from '../components/alerts/PageError';
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import ErrorBoundaryWithNavigate from '../components/errorHandling/ErrorBoundaryWithNavigate';
import { allPageTypes, altPageTypes } from '../enums/allPageTypes';
import { gEnums } from '../enums/globalEnums';
import MotionWait from '../motions/MotionWait';
import { currentHelpers } from '../redirection/current';
import UiWrapper from '../wrappers/UiWrapper';
import PageContent from './PageContent';
import PageItem from './PageItem';
import { PageOverlay } from './PageOverlay';
import { Swiper_PageArea } from 'global/components/swipers/Swiper_PageArea';
import ItemMenuSquares from 'global/wrappers/ItemMenuSquares ';
import { listItemKey } from 'global/lists/lists';

// GOOD TO GO
const AppUserProfilePagesWithProvider = React.lazy(() => import("../components/user/AppUserProfileItems"));
const DirectTicket = React.lazy(() => import("../../projectSpecific/ticketing/components/DirectTicket"));
const EmailSignInConfirmation = React.lazy(() => import("../validation/EmailSignInConfirmation"));
const GolfEventWrapper = React.lazy(() => import("../../projectSpecific/golf/pages/GolfEventWrapper"));
const GolfTournamentWrapper = React.lazy(() => import("../../projectSpecific/golf/pages/GolfTournamentWrapper"));
const HomePage = React.lazy(() => import("./HomePage"));
const ImageMapping = React.lazy(() => import("../components/swipe/ImageMapping"));
const NotificationForeground = React.lazy(() => import("../components/alerts/NotificationForeground"));
const PageAreaContentAlt = React.lazy(() => import("./PageAreaContentAlt"));
const PageMedia = React.lazy(() => import("./PageMedia"));
const SignIn = React.lazy(() => import("../components/auth/SignIn"));
const SportTournamentPage = React.lazy(() => import("../../projectSpecific/sports/pages/SportsTournamentPage"));
const Waiver = React.lazy(() => import("../../globalAlt/page/Waiver"));
// import SportTournamentPage from '../../projectSpecific/sports/pages/SportsTournamentPage';

const _allowHomePage = false
const _showMemos = false

// PageAreaContent > PageContent > PageItem > UiItem > SemUi > SemElems > SemCardsWithProvider

/**
 * 
 * @returns The main content of the app.
 * @description Mostly returns `PageContent`, but depending on certain 'views', it will return something else.
 */
const PageAreaContent = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, handlers } = parentContext ?? {}
  const { currentSettings_state, appNotifications_state, page_state, paps_state, start_state } = states
  const { start_handlers } = handlers
  const { start_fns } = fns
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page, isGlobalStatic, pageSettingsKey, missingSettings } = _.get(page_state, 'pageSettings', {});
  const { startType } = start_state ?? {}

  // appSettingsContext
  const appSettingsContext = useContext(AppSettingsContext)
  const { appSettings_state } = appSettingsContext ?? {}
  const { fetchingSettings } = appSettings_state ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { desktopMode } = framework_state ?? {}
  const { handleShow_signIn } = framework_handlers ?? {}

  // appUser_state  
  const appUserAccess = useAppUserAccess()

  const { vips_all } = usePageItems()
  const { prfItems } = useProfileItems()

  // papsContext 
  const { homePageSeen, ddGroupIndex, nonLandingView, pageKey, pathViews, permissionType, view } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}

  // pageContext  
  const { desktop, homePage, waiver, appNotifications } = aps_global ?? {}
  const { showHomePage, homePageText } = homePage ?? {}
  const { showWaiver, waiverText } = waiver ?? {}
  const { useDefaultLayoutType, defaultLayoutType } = desktop ?? {}
  const { showNotificationsOnForeground } = appNotifications ?? {}

  const { newPayload, notifications_appUser } = appNotifications_state ?? {}
  const { hasNew } = notifications_appUser ?? {}

  // same source 
  const { displayType, viewType, layout: layout_page, desktopLayout: desktopLayout_page } = aps_page ?? {}

  const layout = desktopMode && desktopLayout_page ? desktopLayout_page : layout_page
  const { layoutType, showHiddenToAdmins } = layout ?? {}

  let _layoutType = desktopMode && useDefaultLayoutType && defaultLayoutType ? defaultLayoutType : layoutType
  let _isAdminHidden;

  switch (_layoutType) {
    case gEnums.layoutTypes.swipeableHidden:
      if (showHiddenToAdmins) {
        _layoutType = gEnums.lapyoutTypes.swipeable
        _isAdminHidden = true
      }
      break;
    default:
    // nothing
  }

  const _seenItem_hp = eventKey ? 'homePageSeen-' + eventKey : 'homePageSeen'
  const _homePageSeen = currentHelpers.storageItem_get(_seenItem_hp)

  const seenItem_w = eventKey ? 'waiverSeen-' + eventKey : 'waiverSeen'
  const _waiverSeen = currentHelpers.storageItem_get(seenItem_w)

  // local state
  const [profileItems, setProfileItems] = useState()
  const [pageItemsShown, setViewPageItemsShown] = useState()

  _checkComponentTimes && start_fns.useTimeMeasurement('PageAreaContent', pageItemsShown)

  useEffect(() => {
    if (startType !== startTypes.finish) {
      start_handlers.handleAmmend_startDimmer(startTypes.finish, null, 'pageAreaContent')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [startType, appUserAccess]);

  useEffect(() => {
    // start_handlers.handleAmmend_useEffect('PageAreaContent');
    setProfileItems(prfItems);
    setViewPageItemsShown(vips_all);
  }, [prfItems, vips_all]);

  // const getPageItem = (key, tabIndex, visible) => {
  //   console.log('getPageItem', key)
  //   const _pageItem = <PageItem key={uniqueKey('sipi', key)} uivi={key} tabIndex={tabIndex} returnCount={true} from={'swipeItem_pageItem'} />
  //   return { loaded: true, visible, content: _pageItem }
  // }

  const gpi = (key, tabIndex, visible) => { return { loaded: true, visible: true, content: <div>{key}</div> } }

  const pageAreaSwiper = () => <Swiper_PageArea
    swipeContentType={gEnums.swipeContentTypes.pageAreaContent}
  />

  // const pageAreaSwiper = () => <SwiperWithTabsWithProvider
  //   keyy={'pac'}
  //   swipeContentType={gEnums.swipeContentTypes.pageAreaContent}
  //   view={view}
  //   fn_getContent={gpi}
  //   fromAppUserProfile={false}
  //   layoutType={_layoutType}
  //   isAdminHidden={_isAdminHidden}
  // />

  const memoizedPageContent = useMemo(() => {

    _showMemos && console.log('useMemo > PageAreaContent', pathViews)

    if (pageItemsShown) {
      const isTicketing = !!pathViews.ticketing;
      const isMedia = view === allPageTypes.media;
      const isImageMapping = view === allPageTypes.imageMapping;

      if (fetchingSettings || !_layoutType) {
        return <MotionWait origin={'PageAreaContent'} />
      }

      if (nonLandingView === subAppTypes.emailSignInConfirmation) {
        return <EmailSignInConfirmation origin={'pageAreaContent'} />;
      } else if (isTicketing) {
        return <DirectTicket />;
      } else if (isMedia) {
        return <PageMedia />;
      } else if (isImageMapping) {
        return <ImageMapping imageMapType={gEnums.imageMapTypes.zoneMapping} />;
      } else {
        if (_allowHomePage && !_homePageSeen && !homePageSeen && showHomePage && homePageText) {
          return <HomePage />;
        } else if (!_waiverSeen && showWaiver && waiverText) {
          return <Waiver />;
        } else {
          if (altPageTypes[view]) {
            return <PageAreaContentAlt view={view} />;
          } else {
            // Fallback based on viewType, displayType, permissionType, and _layoutType.
            switch (viewType) {
              default:
                switch (displayType) {
                  case gEnums.viewDisplayTypes.signIn:
                    return <SignIn origin={'pageAreaContent'} handleCancel={handleShow_signIn} />;

                  case gEnums.viewDisplayTypes.appProfile:
                  case allPageTypes.appUserInfo:
                    return UiWrapper(<AppUserProfilePagesWithProvider />);

                  default:
                    switch (permissionType) {
                      default:
                        switch (_layoutType) {
                          case gEnums.layoutTypes.swipeable:
                          case gEnums.layoutTypes.swipeableHidden:
                            const viCount = pageItemsShown ? Object.keys(pageItemsShown).length : 0
                            if (viCount > 1 && !ddGroupIndex) {
                              return pageAreaSwiper();
                            } else {
                              return <PageContent />;
                            }

                          case gEnums.layoutTypes.squares:
                            // case gEnums.layoutTypes.icons:
                            const hc = (item) => {
                              framework_handlers.handleClick_contentItem({ clickedItem: item, itemKey: item.key })
                            }
                            return <ItemMenuSquares menuItems={pageItemsShown} onClick={hc} isFull={true} />
                          // return <PageContent />;

                          case gEnums.layoutTypes.horizontalSliders:
                          case gEnums.layoutTypes.icons:
                          case gEnums.layoutTypes.pageIcons:
                          case gEnums.layoutTypes.pageList:
                          case gEnums.layoutTypes.singlePage:
                            return <PageContent />;

                          case gEnums.layoutTypes.golfEvent:
                            return <GolfEventWrapper />;

                          case gEnums.layoutTypes.golfTournament:
                            return <GolfTournamentWrapper />;

                          case gEnums.layoutTypes.sportTournament:
                            return <SportTournamentPage sportsTournamentViewMode={_sportsTournamentViewModes.viewTournament} />;

                          default:
                            return <PageError reason={pageErrorReasonTypes.noLayoutType} />;
                        }
                    }
                }
            }
          }
        }
      }
    } else {
      return <div></div>
    }
  }, [
    _layoutType,
    pageItemsShown,
    pathViews,
    permissionType,
    view,
    viewType,
    appUserAccess,
    // Include any additional dependencies used inside the logic
  ]);

  const content = () => <ErrorBoundaryWithNavigate origin={'pageAreaContent'}>
    <PageDataProvider>
      <PageAreaProvider
        profileItems={profileItems}
        pageItemsShown={pageItemsShown}
      >
        <PageAreaParentProvider>
          <RssProvider>
            <Suspense fallback={<SuspenseDimmer origin={'Page Area Content'} />}>
              {/* <PageOverlay /> */}
              {memoizedPageContent}
              {(isGlobalStatic || missingSettings) && <PageError reason={pageErrorReasonTypes.noSettings} />}
              {(showNotificationsOnForeground && (newPayload || hasNew)) && <NotificationForeground />}
            </Suspense>
          </RssProvider>
        </PageAreaParentProvider>
      </PageAreaProvider>
    </PageDataProvider>
  </ErrorBoundaryWithNavigate>

  // IMPORTANT: Wait for the pageSetting to be correct
  if (pageItemsShown && pageSettingsKey === pageKey) {
    return content()
  } else {
    return <MotionWait origin={'pageAreaContent'} />
  }
}

export default PageAreaContent